/** Formats a duration in seconds as "Xs" or "Xm Ys". */
export function formatElapsed(seconds: number): string {
  if (!Number.isFinite(seconds)) return '';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

/** Formats a token count compactly: 745 → "745", 38382 → "38.4k". */
export function formatTokenCount(count: number): string {
  if (count < 1000) return count.toString();
  if (count < 10_000) return (count / 1000).toFixed(1) + 'k';
  if (count < 1_000_000) return Math.round(count / 1000) + 'k';
  return (count / 1_000_000).toFixed(1) + 'M';
}

/** Returns true if the trimmed string starts with `{` or `[`. */
export function isJsonLike(s: string): boolean {
  return /^\s*[{[]/.test(s);
}

/** Formats a byte count as a human-readable file size (e.g. "1.5 MB"). */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Formats a timestamp the way macOS Finder does:
 *   "Today at 4:01 PM" / "Yesterday at 11:53 PM" / "May 22, 2026 at 1:20 PM"
 *
 * "Today" / "Yesterday" are computed against the local calendar date.
 * Older timestamps include the full month-day-year. Locale follows the
 * user's browser preference (English-style output for `en-*` locales).
 */
export function formatAppleDate(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const now = new Date();
  const timePart = then.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 86_400_000;
  const thenDayStart = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
  const dayDiff = Math.round((thenDayStart - startOfToday) / dayMs);
  if (dayDiff === 0) return `Today at ${timePart}`;
  if (dayDiff === -1) return `Yesterday at ${timePart}`;
  const datePart = then.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return `${datePart} at ${timePart}`;
}
